import dynamic from "next/dynamic";
import { type FC, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import NoSSR from "react-no-ssr";

import type { DocumentMetadata } from "@moment/api-collab/api-types";

import { isDesktopAppMode } from "~/auth/interop/common";
import type { EaselProps } from "~/components/canvas/easel/Easel";

import { usePageEditorState } from "../easel/usePageEditorState";
import { BigRichTextEditor } from "../editor/BigRichTextEditor";
import { CanvasFailure } from "./CanvasFailure";
import { PublishedDocument } from "./PublishedDocument";
import { TemplateVitePressDocs } from "./TemplateVitePressDocs";

export interface CanvasPageProps {
	workspaceName: string;
	documentMetadata: DocumentMetadata;
	activePageId: string;
}

const Easel = dynamic<EaselProps>(
	() => import("~/components/canvas/easel/Easel").then((m) => m["Easel"]),
	{ ssr: true, loading: () => <></> }
);

const Canvas = dynamic(() => import("~/components/canvas/views/Canvas").then((m) => m.Canvas), {
	ssr: false,
	loading: () => <></>,
});

export const CanvasPage: FC<CanvasPageProps> = (props) => {
	const { documentMetadata, activePageId } = props;

	const { editorState } = usePageEditorState(documentMetadata.id, activePageId);

	let publishedDocument: ReactNode;
	switch (documentMetadata.publishSettings?.template) {
		case "blog":
			publishedDocument = <PublishedDocument canvas={props.documentMetadata} />;
			break;
		default:
			publishedDocument = <TemplateVitePressDocs canvas={props.documentMetadata} />;
			break;
	}

	const documentInteractionMode: "static" | "interactive" = isDesktopAppMode()
		? "interactive"
		: "static";

	return (
		<ErrorBoundary FallbackComponent={CanvasFailure}>
			<Easel
				workspaceName={props.workspaceName}
				documentId={props.documentMetadata.id}
				pageId={props.activePageId}
				documentInteractionMode={documentInteractionMode}
			>
				<BigRichTextEditor document={props.documentMetadata} editorState={editorState}>
					{isDesktopAppMode() ? (
						<NoSSR>
							<Canvas canvas={props.documentMetadata} />
						</NoSSR>
					) : (
						publishedDocument
					)}
				</BigRichTextEditor>
			</Easel>
		</ErrorBoundary>
	);
};
